/**
 * ROLE-LENS-DESIGN-SYSTEM-V1 -- ErrorState: the section-level failure
 * wrapper (WHAT failed, WHY -- the domain message verbatim, and WHAT TO DO
 * NEXT -- one caller-passed affordance). Composes the EXISTING EmptyState
 * untouched (charter order: reuse, never a competitor).
 *
 * SOURCE PATTERN (extraction register): design-language 03 P11 ERROR (the
 * S4 single error contract -- "one shared block rendering the domain
 * message verbatim ... always with a Retry affordance; never a blank list
 * on failure") as practiced by the feedback blocks on the operator
 * surfaces; error-passthrough law LES-009 (server 400/409 messages render
 * VERBATIM; raw 500 internals never surface -- the CALLER selects the
 * domain message, this component never rewrites it). Proof status: the S4
 * error contract is ratified design law (03 P11); no single founder-walked
 * source screen owns it -- IMPLEMENTATION REFERENCE, UNVALIDATED.
 *
 * COMPETITOR FENCE: page/route-level LOAD + auth errors stay owned by
 * frontend/src/dashboard/DashboardLoadError.tsx (registry
 * component.dashboard-load-error -- the status-driven 404/401/403 card).
 * ErrorState is the IN-FLOW section failure wrapper; it never classifies
 * HTTP statuses and never replaces that card. The FeedbackBanner family is
 * explicitly OUT of scope (charter).
 *
 * AUDIENCE RULE (fail closed, per ui/role-lens.ts): the failure HEADLINE
 * and the next-step affordance are role-neutral (an error must never
 * render as a blank hole for anyone). The DETAIL line (the domain message,
 * which can name record specifics) renders only for a KNOWN audience when
 * an audience is supplied; an EXPLICITLY UNKNOWN audience withholds the
 * detail. Omitting the audience prop keeps the detail role-neutral -- the
 * DashboardLoadError precedent (errors carry no audience) stays lawful.
 *
 * CLINICAL WIRE LAW (CLAUDE.md "Clinical visibility"): presentation, never
 * the security boundary; the caller passes only messages its wire lawfully
 * carried, and server fences remain the authority.
 */

import type { ReactNode } from 'react';
import EmptyState from './EmptyState';
import { isKnownAudience, type RoleLensAudience } from './role-lens';

export interface ErrorStateProps {
  /** WHAT failed -- the section's own plain-language headline. */
  readonly title: string;
  /** WHY -- the domain message VERBATIM (LES-009); optional. */
  readonly detail?: string | null;
  /** WHAT TO DO NEXT -- one affordance (e.g. a Retry button). */
  readonly action?: ReactNode;
  /** Optional audience: when supplied and UNKNOWN, the detail line is
   *  withheld (fail closed); when omitted, the detail is role-neutral. */
  readonly audience?: RoleLensAudience | string | null;
  readonly testId?: string;
}

/** THE pure detail-visibility decision (pinned in tests). */
export function errorDetailVisible(
  audience: ErrorStateProps['audience'],
  detail: ErrorStateProps['detail'],
): boolean {
  if (detail === undefined || detail === null || detail === '') return false;
  if (audience === undefined) return true;
  return isKnownAudience(audience);
}

export default function ErrorState({
  title,
  detail,
  action,
  audience,
  testId,
}: ErrorStateProps): JSX.Element {
  const showDetail = errorDetailVisible(audience, detail);
  return (
    <div className="ac-errorstate" role="alert" data-testid={testId}>
      <EmptyState
        title={title}
        body={
          showDetail ? (
            <span className="ac-errorstate__detail" data-testid="errorstate-detail">
              {detail}
            </span>
          ) : undefined
        }
        action={action}
      />
    </div>
  );
}
